import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

/**
 * Document Manager — upload, list, confirm AI-parsed documents.
 * W2 Sprint 3: Belegmanagement foundation.
 */
export default function DocumentManager({ token, onClose }) {
  const { t } = useTranslation();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [uploading, setUploading] = useState(false);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const url = filter === 'all'
        ? '/api/v1/aif/documents'
        : `/api/v1/aif/documents?document_type=${filter}`;
      const result = await apiClient.get(url);
      if (result.data) setDocs(result.data.documents || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchDocs(); }, [filter]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'SONSTIGE');
      await apiClient.post('/api/v1/aif/documents/upload', formData, {
        queueOnOffline: true,
        entityType: 'document',
      });
      fetchDocs();
    } catch { /* ignore */ }
    setUploading(false);
  };

  const confirmDoc = async (id) => {
    await apiClient.patch(`/api/v1/aif/documents/${id}/confirm`).catch(() => {});
    fetchDocs();
  };

  const deleteDoc = async (id) => {
    await apiClient.delete(`/api/v1/aif/documents/${id}`).catch(() => {});
    fetchDocs();
  };

  const TYPE_ICONS = { RECHNUNG: '\uD83D\uDCCB', LIEFERSCHEIN: '\uD83D\uDE9A', QUITTUNG: '\uD83E\uDDFE', BODENPROBE: '\uD83E\uDDEA', VERTRAG: '\uD83D\uDCC4', SACHKUNDE: '\uD83C\uDF93', FOTO: '\uD83D\uDCF8', SONSTIGE: '\uD83D\uDCC1' };
  const STATUS_BADGE = { PENDING: 'bg-gray-100 text-gray-600', PARSING: 'bg-blue-100 text-blue-700', PARSED: 'bg-amber-100 text-amber-700', CONFIRMED: 'bg-green-100 text-green-700', FAILED: 'bg-red-100 text-red-700' };

  const FILTERS = ['all', 'RECHNUNG', 'LIEFERSCHEIN', 'BODENPROBE', 'VERTRAG'];

  return (
    <div className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col animate-slideInRight">
      <header className="flex items-center justify-between px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
          </button>
          <span className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('documents.title')}</span>
        </div>
        <label className="px-3 py-1.5 rounded-lg bg-[var(--aif-color-primary)] text-white text-xs font-medium cursor-pointer">
          + {t('documents.upload')}
          <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleUpload} />
        </label>
      </header>

      {/* Filter chips */}
      <div className="flex gap-2 px-4 py-2 overflow-x-auto flex-shrink-0">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition ${filter === f ? 'bg-[var(--aif-color-primary)] text-white' : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-secondary)]'}`}>
            {f === 'all' ? t('share.all') : f}
          </button>
        ))}
      </div>

      {/* Document list */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {uploading && <div className="text-center py-4 text-sm text-[var(--aif-text-muted)]">{t('documents.uploading')}</div>}
        {loading ? (
          <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" /></div>
        ) : docs.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-3xl mb-2">{'\uD83D\uDCC1'}</div>
            <p className="text-sm text-[var(--aif-text-muted)]">{t('documents.empty')}</p>
          </div>
        ) : docs.map(doc => (
          <div key={doc.id} className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-3 space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-lg">{TYPE_ICONS[doc.document_type] || '\uD83D\uDCC1'}</span>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[var(--aif-text-primary)] truncate">{doc.title || doc.original_filename}</div>
                  <div className="text-[10px] text-[var(--aif-text-muted)]">{doc.document_type} &middot; {new Date(doc.created_at).toLocaleDateString('de-DE')}</div>
                </div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[doc.parse_status] || ''}`}>
                {doc.parse_status}
              </span>
            </div>
            {doc.parse_status === 'PARSED' && (
              <div className="flex gap-2 justify-end pt-1">
                <button onClick={() => confirmDoc(doc.id)} className="text-xs text-[var(--aif-color-primary)] font-medium">{t('documents.confirm')}</button>
                <button onClick={() => deleteDoc(doc.id)} className="text-xs text-[var(--aif-color-danger)]">{t('documents.delete')}</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
